/* eslint-disable import/no-cycle */
import { createActions } from 'redux-actions';
import { batch } from 'react-redux';

import { staticDataHelper } from '../../../config';
import registrationApi from '../../../api/registrationApi';
import {
  markCurrentStepAsCompletedAndGoToNextStep,
  divertProgress,
  appendToActiveStepLinks,
  gotoNextStep as nextStep,
} from '../steps/actions';
import { updateDirectorList } from '../idverification/actions';
import { showMainLoading, showMainLoadingAsSuccess, hideMainLoading } from '../main/actions';
import {
  selectMobileVerification,
  selectWorkflowActiveStepLinkByRel,
  selectMainPortal,
  selectUserJourneyAccountType,
} from '../../../selectors';
import Auth from '../../../api/auth';
import phoneNumberUtils from '../../../utils/phoneNumberUtils';
import { ACTION_PREFIX, actionTypes } from './constants';
import { getActionOptions } from '../../../utils/reduxactionsutils';
import {
  GTM_EVENT_TYPE_USER_JOURNEY,
  GTM_USER_JOURNEY_FIELD_NAME,
  GTM_USER_JOURNEY_ERROR_TYPE,
  NETWORK_CONNECTION_ERROR_MESSAGE,
  NETWORK_ERROR,
} from '../../../constants';
import { gtmUserJourneyEvent, getMarketingTagsMapped } from '../../../utils/gtmUtils';

let resendLoginCodeTimerId;

export const {
  loginCodeSent,
  updateLoginCodeSendingFailure,
  updateMobileValidationResult,
  resetValidation,
  updateCanResendLoginCode,
  reset,
} = createActions(
  actionTypes.LOGIN_CODE_SENT,
  actionTypes.UPDATE_LOGIN_CODE_SENDING_FAILURE,
  actionTypes.UPDATE_MOBILE_VALIDATION_RESULT,
  actionTypes.RESET_VALIDATION,
  actionTypes.UPDATE_CAN_RESEND_LOGIN_CODE,
  actionTypes.RESET,
  getActionOptions(ACTION_PREFIX),
);

export const clearTimer = () => {
  if (resendLoginCodeTimerId > -1) {
    clearTimeout(resendLoginCodeTimerId);
    resendLoginCodeTimerId = -1;
  }
};

export function sendLoginCode(mobileNumber, callback) {
  clearTimer();

  return (dispatch, getState) => {
    const state = getState();
    const portal = selectMainPortal(state);
    const stepLink = selectWorkflowActiveStepLinkByRel(state, 'Mobile');
    const userJourneyAccountType = selectUserJourneyAccountType(state);

    resendLoginCodeTimerId = setTimeout(() => dispatch(updateCanResendLoginCode(true)), 60000);

    batch(() => {
      dispatch(showMainLoading('Sending verification code...'));
      dispatch(resetValidation());
      dispatch(
        updateLoginCodeSendingFailure({
          loginCodeSendingFailed: true,
          message: null,
        }),
      );
      dispatch(updateCanResendLoginCode(false));
    });

    const formattedMobileNumber = phoneNumberUtils.formatMobilePhoneNumber(mobileNumber);
    registrationApi
      .verifyMobileNumber(stepLink.href, formattedMobileNumber, portal)
      .then((response) => {
        if (callback) {
          callback();
        }
        const { output } = response.data;
        if (!output.isError) {
          const gtmUserJourneyEventObject = {
            eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_MOBILE_ADDED,
            accountType: userJourneyAccountType,
          };
          gtmUserJourneyEvent(gtmUserJourneyEventObject);
          batch(() => {
            dispatch(
              loginCodeSent({
                mobile: output.mobile,
                codeExpiry: output.validityInSeconds,
              }),
            );
            dispatch(appendToActiveStepLinks(output.links));
          });
        } else {
          batch(() => {
            dispatch(
              updateLoginCodeSendingFailure({
                loginCodeSendingFailed: true,
                message: output.errorMessage,
              }),
            );
            dispatch(updateCanResendLoginCode(true));
          });
        }
      })
      // eslint-disable-next-line no-unused-vars
      .catch((_reason) => {
        if (callback) {
          callback();
        }
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        batch(() => {
          dispatch(
            updateLoginCodeSendingFailure({
              loginCodeSendingFailed: true,
              message: networkError || 'Something went wrong while sending the verification code',
            }),
          );
          dispatch(updateCanResendLoginCode(true));
        });
      })
      .finally(() => {
        dispatch(hideMainLoading());
      });
  };
}

export function confirmLoginCode(pin, loggedInUser, callback) {
  return async (dispatch, getState) => {
    const state = getState();
    const { mobileNumber, validationResult } = selectMobileVerification(state);
    const stepLink = selectWorkflowActiveStepLinkByRel(state, 'SMS');
    const userJourneyAccountType = selectUserJourneyAccountType(state);

    dispatch(showMainLoading('Validating verification code...'));
    const marketingTagsMapped = await getMarketingTagsMapped();

    registrationApi
      .confirmMobileNumber(stepLink.href, mobileNumber, pin, marketingTagsMapped)
      .then(async (response) => {
        if (callback) {
          callback();
        }
        // we are calling hide main loading here rather than in finally block as next page has api call in componentDidMount
        dispatch(hideMainLoading());
        const { output } = response.data;
        const { isError, links, accessToken, expiresIn, tokenType, officeHolders } = output;

        if (links && links.length) {
          const link = links[0];
          if ((link.rel === 'Completed' || link.rel === 'Manual') && link.href) {
            dispatch(showMainLoadingAsSuccess('Success! Please wait while we redirect you...'));
            setTimeout(() => {
              // eslint-disable-next-line no-undef
              window.location = link.href;
            }, 3000);
            return;
          }
          if (link.rel === 'ThankYou' || (link.rel && !link.href)) {
            batch(() => {
              dispatch(hideMainLoading());
              const successMessage =
                'Thank you for entering your details. We will call you shortly to complete your registration.';
              dispatch(
                divertProgress({
                  isBlocked: false,
                  isSuccess: true,
                  message: successMessage,
                }),
              );
            });
          }
        }
        if (!isError) {
          batch(() => {
            dispatch(
              updateMobileValidationResult({
                isValidated: true,
                isMobileConfirmed: true,
                message: 'Mobile number has been verified successfully',
                failureCount: 0,
              }),
            );

            const gtmUserJourneyEventObject = {
              eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_MOBILE_VERIFIED,
              accountType: userJourneyAccountType,
            };
            gtmUserJourneyEvent(gtmUserJourneyEventObject);

            if (officeHolders && officeHolders.length) {
              dispatch(updateDirectorList(officeHolders));
            }

            // don't replace the token from AD for internal users
            if (!loggedInUser) {
              Auth.loggedIn(accessToken, expiresIn, tokenType);
            }

            if (links && links.length) {
              dispatch(markCurrentStepAsCompletedAndGoToNextStep(links));
            }
          });
        } else {
          // TODO: this validation should be done by the server.
          const failureCount = validationResult.failureCount + 1;

          const gtmUserJourneyEventObject = {
            eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_MOBILE_VERIFIED,
            accountType: userJourneyAccountType,
            fieldName: GTM_USER_JOURNEY_FIELD_NAME.MOBILE_CODE_VALIDATION,
            isError: true,
            errorType: GTM_USER_JOURNEY_ERROR_TYPE.INCORRECT_CODE_ENTERED,
          };
          gtmUserJourneyEvent(gtmUserJourneyEventObject);

          if (failureCount === staticDataHelper.loginCodeRetryCount) {
            const blockMessage =
              'You have entered wrong code that was sent to ' +
              `${mobileNumber} for ${staticDataHelper.loginCodeRetryCount} times. ` +
              `Please call ${staticDataHelper.hotline} if you are having trouble with registration.`;
            dispatch(
              divertProgress({
                isBlocked: true,
                isSuccess: false,
                message: blockMessage,
              }),
            );
            return;
          }

          dispatch(
            updateMobileValidationResult({
              isValidated: true,
              isMobileConfirmed: false,
              message: output.errorMessage,
              failureCount,
            }),
          );
        }
      })
      // eslint-disable-next-line no-unused-vars
      .catch((_reason) => {
        if (callback) {
          callback();
        }
        const networkError =
          _reason.message && _reason.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        dispatch(hideMainLoading());
        dispatch(
          updateMobileValidationResult({
            isValidated: true,
            isMobileConfirmed: false,
            message: networkError || 'Something went wrong while validating the verification code',
          }),
        );
      })
      .finally(() => {});
  };
}

export function gotoNextStep() {
  return (dispatch) => {
    clearTimer();
    dispatch(nextStep());
  };
}
